import { ReactElement, useState } from 'react';
import { IconCircleMinus } from '@rossum/ui/icons/tabler';
import {
  Chip,
  Popover,
  Skeleton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { useIntl } from '../../ComponentsContext';
import { FilterSettings, FiltersObject } from '../types';

type FilterChipsProps<O extends FiltersObject> = {
  filterSetting: FilterSettings<O>;
  filters: O;
  isValid: boolean;
  renderPlaceholderChip?: ({
    onClick,
  }: {
    onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  }) => ReactElement;
};

export const FilterChip = <O extends FiltersObject>({
  filterSetting,
  filters,
  isValid,
  renderPlaceholderChip,
}: FilterChipsProps<O>) => {
  const intl = useIntl();

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  return (
    <>
      <Tooltip
        title={
          isValid
            ? intl.formatMessage({
                id: 'containers.filtering.chip.invalidValueTooltip',
              })
            : ''
        }
        placement="top"
        slotProps={{
          tooltip: {
            sx: { maxHeight: 100, overflowY: 'auto' },
          },
          arrow: { sx: { display: 'none' } },
        }}
      >
        {renderPlaceholderChip ? (
          renderPlaceholderChip({ onClick: e => setAnchorEl(e.currentTarget) })
        ) : (
          <Chip
            color={isValid ? 'warning' : 'default'}
            onClick={e => setAnchorEl(e.currentTarget)}
            label={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="body2">{filterSetting.name}:</Typography>
                <Stack direction="row">
                  {filterSetting.optionsAreLoading ? (
                    <Skeleton variant="text" width={50} />
                  ) : (
                    <Typography
                      variant="body2"
                      maxWidth={150}
                      textOverflow="ellipsis"
                      overflow="hidden"
                      fontWeight="bold"
                    >
                      {isValid
                        ? intl.formatMessage({
                            id: 'containers.filtering.chip.invalidValue',
                          })
                        : filterSetting.getChipLabel(
                            filterSetting.valueGetter(filters)
                          )}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            }
            onDelete={() => filterSetting.setFilter(undefined)}
            deleteIcon={
              <SvgIcon
                fontSize="large"
                data-cy={`${filterSetting.filterKey}-filter-chip-delete-icon`}
              >
                <IconCircleMinus />
              </SvgIcon>
            }
          />
        )}
      </Tooltip>
      <Popover
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      >
        <filterSetting.Component
          {...filterSetting}
          onClose={() => setAnchorEl(null)}
          confirmFilter={filterSetting.setFilter}
          currentValue={filterSetting.valueGetter(filters)}
        />
      </Popover>
    </>
  );
};
